import { Suspense, useEffect, useMemo, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, RoundedBox } from '@react-three/drei';
import { Dialog } from 'radix-ui';
import { Box, X } from 'lucide-react';
import * as THREE from 'three';
import { readingKey, useTwin } from '../twin/store';
import { useNowMs } from '../twin/ui/hooks/useNowMs';
import { F200_DATASHEET_URL, F200_DIMENSIONS_M, F200_MIB_URL, F200_PORTS } from './f200';
import cards from './cards.json';

type Port = (typeof F200_PORTS)[number]['id'];

function MonitorModel({ selected, choose }: { selected: Port; choose: (port: Port) => void }) {
  const d = F200_DIMENSIONS_M;
  const label = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 400;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#15191d';
    ctx.fillRect(0, 0, 1024, 400);
    ctx.fillStyle = '#edf4f6';
    ctx.font = 'bold 126px sans-serif';
    ctx.fillText('RLE', 58, 167);
    ctx.font = 'bold 68px sans-serif';
    ctx.fillText('F200', 62, 250);
    ctx.font = '26px sans-serif';
    ctx.fillText('ENVIRONMENTAL MONITOR', 390, 168);
    ctx.fillStyle = '#9cabb5';
    ctx.font = '23px sans-serif';
    ctx.fillText('4 SENSOR INPUTS  ·  8 DIGITAL INPUTS', 390, 217);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }, []);
  useEffect(() => () => label.dispose(), [label]);
  // This isolated product view has no geographic placement or assumed attached wiring.
  return (
    <group>
      <RoundedBox args={[d.width, d.height, d.depth]} radius={0.0012} smoothness={3}>
        <meshStandardMaterial color="#20262c" roughness={0.48} metalness={0.65} />
      </RoundedBox>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, d.height / 2 + 0.0001, 0]}>
        <planeGeometry args={[0.13, 0.048]} />
        <meshBasicMaterial map={label} />
      </mesh>
      {[-1, 1].flatMap((side) =>
        [-1, 1].map((end) => (
          <mesh
            key={`${side}-${end}`}
            position={[side * 0.067, 0.006, end * 0.034]}
            rotation={[Math.PI / 2, 0, 0]}
          >
            <cylinderGeometry args={[0.0012, 0.0012, 0.001, 12]} />
            <meshStandardMaterial color="#818d94" metalness={0.8} roughness={0.4} />
          </mesh>
        )),
      )}
      <group>
        {[-0.05, -0.033, -0.016, 0.001].map((x, index) => (
          <group
            key={x}
            position={[x, -0.002, 0.034]}
            onClick={
              index === 0
                ? (e) => {
                    e.stopPropagation();
                    choose('sensor1');
                  }
                : undefined
            }
          >
            <mesh>
              <boxGeometry args={[0.014, 0.014, 0.004]} />
              <meshStandardMaterial
                color={index === 0 && selected === 'sensor1' ? '#75dfe1' : '#adbbc1'}
                metalness={0.7}
                roughness={0.4}
              />
            </mesh>
            <mesh position={[0, 0, 0.0021]}>
              <planeGeometry args={[0.011, 0.01]} />
              <meshBasicMaterial color="#070c10" />
            </mesh>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <mesh key={i} position={[(i - 2.5) * 0.0014, -0.003, 0.0022]}>
                <boxGeometry args={[0.00045, 0.004, 0.0003]} />
                <meshStandardMaterial color="#b7a46d" metalness={0.8} roughness={0.3} />
              </mesh>
            ))}
          </group>
        ))}
      </group>
      <mesh position={[0.053, -0.002, 0.034]}>
        <boxGeometry args={[0.016, 0.014, 0.004]} />
        <meshStandardMaterial color="#9da9ae" metalness={0.8} roughness={0.3} />
      </mesh>
      <mesh position={[0.053, -0.002, 0.0362]}>
        <planeGeometry args={[0.013, 0.011]} />
        <meshBasicMaterial color="#080c10" />
      </mesh>
      <mesh position={[0.032, -0.001, 0.034]}>
        <boxGeometry args={[0.007, 0.004, 0.002]} />
        <meshStandardMaterial color="#7c8b91" metalness={0.9} />
      </mesh>
      {(
        [
          { x: -0.043, w: 0.035, id: 'digital8' },
          { x: -0.005, w: 0.035, id: 'digital8' },
          { x: 0.034, w: 0.018, id: 'relay' },
          { x: 0.06, w: 0.012, id: 'leak' },
        ] as const
      ).map((p, i) => (
        <group
          key={i}
          position={[p.x, -0.003, -0.035]}
          onClick={(e) => {
            e.stopPropagation();
            choose(p.id);
          }}
        >
          <mesh>
            <boxGeometry args={[p.w, 0.012, 0.008]} />
            <meshStandardMaterial color={selected === p.id ? '#a8e749' : '#709f72'} roughness={0.65} />
          </mesh>
          {Array.from({ length: i < 2 ? 8 : i === 2 ? 3 : 2 }, (_, j) => (
            <mesh
              key={j}
              position={[-p.w / 2 + 0.002 + (j * (p.w - 0.004)) / (i < 2 ? 7 : i === 2 ? 2 : 1), 0, -0.0041]}
            >
              <boxGeometry args={[0.002, 0.005, 0.0005]} />
              <meshBasicMaterial color="#18382b" />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
}

export function F200Inspector() {
  const [open, setOpen] = useState(false);
  const [selected, choose] = useState<Port>('sensor1');
  const readings = useTwin((s) => s.readings);
  const name = useTwin((s) => s.element?.callsign ?? s.assetId);
  const now = useNowMs(2000, true);
  const port = F200_PORTS.find((p) => p.id === selected)!;
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button className="mt-3 flex items-center gap-2 rounded-control border border-accent/40 px-3 py-2 text-accent">
          <Box size={15} />
          Inspect F200 monitor
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[90] bg-black/65" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[91] flex max-h-[90vh] w-[min(1080px,95vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-panel border border-hairline bg-canvas shadow-2xl">
          <div className="flex items-start justify-between border-b border-hairline p-4">
            <div>
              <Dialog.Title className="font-semibold text-ink">RLE F200 · environmental monitor</Dialog.Title>
              <Dialog.Description className="mt-1 text-xs text-ink-3">
                {name} · manufacturer-dimensioned product reference. Connector detail is representative;
                installation location and wiring are unknown.
              </Dialog.Description>
            </div>
            <Dialog.Close aria-label="Close F200 inspector" className="p-1 text-ink-2">
              <X size={20} />
            </Dialog.Close>
          </div>
          <div className="grid min-h-0 overflow-y-auto md:grid-cols-[1fr_320px]">
            <div className="relative h-[380px] bg-[#10191f] md:h-[490px]">
              <Canvas camera={{ position: [0.17, 0.14, 0.19], fov: 38, near: 0.001, far: 5 }} dpr={[1, 1.5]}>
                <ambientLight intensity={1.3} />
                <directionalLight position={[0.3, 0.5, 0.3]} intensity={3} />
                <directionalLight position={[-0.3, 0.2, -0.3]} intensity={2} color="#98bbc8" />
                <Suspense fallback={null}>
                  <MonitorModel selected={selected} choose={choose} />
                </Suspense>
                <OrbitControls makeDefault minDistance={0.12} maxDistance={0.5} enablePan={false} />
              </Canvas>
              <p className="absolute top-3 left-3 text-[11px] text-ink-2">
                143 × 25 × 67 mm · product reference
              </p>
              <p className="absolute bottom-3 left-3 text-[11px] text-ink-3">
                Drag to rotate · scroll to zoom · select a connector group
              </p>
            </div>
            <div className="space-y-3 border-l border-hairline p-4 text-xs">
              <div className="flex flex-wrap gap-1">
                {F200_PORTS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => choose(p.id)}
                    aria-pressed={selected === p.id}
                    className={`rounded-control border px-2 py-1.5 ${selected === p.id ? 'border-accent text-accent' : 'border-hairline text-ink-2'}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <h3 className="font-semibold text-ink">{port.label}</h3>
              <p className="text-ink-2">{port.note}</p>
              <p className="break-words font-mono text-[10px] text-ink-3">{port.field}</p>
              {port.readings.map((id) => {
                const r = readings[readingKey('environmental', id)];
                const measured = r?.source === 'live';
                const label = cards[0].items.find((row) => row[0] === id)?.[1] ?? id;
                return (
                  <div key={id} className="rounded-control border border-hairline p-2">
                    <p className="text-ink-3">{label}</p>
                    <p className="mt-1 font-mono text-ink">{measured ? r.value : 'Unavailable'}</p>
                    {measured && (
                      <p className="mt-1 text-[10px] text-ink-3">
                        {now - r.timeMs > 120000 ? 'Stale · ' : ''}
                        {new Date(r.timeMs).toISOString()}
                      </p>
                    )}
                  </div>
                );
              })}
              <p className="text-[11px] text-ink-3">
                Other sockets show product capabilities. No fitted probe, load or cable is implied. LEDs are
                unlit reference geometry; no power or health state is inferred.
              </p>
              <a
                href={F200_DATASHEET_URL}
                target="_blank"
                rel="noreferrer"
                className="block text-accent underline"
              >
                Manufacturer dimensions and photographs
              </a>
              <a href={F200_MIB_URL} target="_blank" rel="noreferrer" className="block text-accent underline">
                Manufacturer SNMP field definitions
              </a>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
